<script setup lang="ts">
/**
 * What the enterprise's memory recalled for one of your messages, under that message. The line says how many
 * notes were recalled; opened, it lists each one by its first line, says whether the member was shown it, and
 * links to where the note lives: its entry on the Memory page, or the run whose lesson it is.
 *
 * It sits inside a ZhChatThread bubble beside the tool log, so it speaks the same language (ChatToolLog): the
 * shared system label for its summary, statuses and links, a hairline above, and no type of its own.
 */
import { ZhSystemLabel } from '@zerohuman/ui'
import { RouterLink } from 'vue-router'
import type { RecalledNote } from '@/lib/chat'

defineProps<{ summary: string; notes: RecalledNote[] }>()
</script>

<template>
	<div data-testid="chat-memory-log" class="chat-memory-log">
		<ZhSystemLabel v-if="notes.length === 0" data-testid="chat-memory-note" tone="muted">
			{{ summary }}
		</ZhSystemLabel>
		<details v-else>
			<ZhSystemLabel as="summary" data-testid="chat-memory-note" tone="muted" class="chat-memory-log__summary">
				{{ summary }}
			</ZhSystemLabel>
			<ol class="chat-memory-log__notes">
				<li v-for="note in notes" :key="note.key" class="chat-memory-log__note" data-testid="chat-memory-recalled">
					<span class="chat-memory-log__title">{{ note.title }}</span>
					<span class="chat-memory-log__line">
						<ZhSystemLabel as="span" tone="muted">{{ note.statusLabel }}</ZhSystemLabel>
						<RouterLink v-if="note.to" class="chat-memory-log__open" :to="note.to">
							<ZhSystemLabel as="span" tone="system">{{ note.openLabel }}</ZhSystemLabel>
						</RouterLink>
					</span>
				</li>
			</ol>
		</details>
	</div>
</template>

<style scoped>
ol {
	margin: 0;
	padding: 0;
	list-style: none;
}

/* Under the message, set off by the hairline the tool log uses (ChatToolLog `.chat-tool-log`). */
.chat-memory-log {
	margin-top: 0.75rem;
	padding-top: 0.75rem;
	border-top: 1px solid var(--hair);
	color: var(--ink-2);
}

.chat-memory-log__summary {
	cursor: pointer;
	user-select: none;
}

.chat-memory-log__notes {
	display: grid;
	gap: 0.5rem;
	margin-top: 0.5rem;
}

.chat-memory-log__note {
	display: grid;
	gap: 0.125rem;
}

/* A note's first line can be a long path: it breaks rather than widening the bubble. */
.chat-memory-log__title {
	color: var(--ink-1);
	overflow-wrap: anywhere;
}

.chat-memory-log__line {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	column-gap: 0.75rem;
	row-gap: 0.25rem;
}

.chat-memory-log__open {
	text-decoration: none;
}

.chat-memory-log__open:hover,
.chat-memory-log__open:focus-visible {
	text-decoration: underline;
	text-underline-offset: 0.2em;
}
</style>
